import { useBemCN } from "@gik/core/utils/bemBlock";
import { Input } from "@gik/ui-legacy/Input";
import React from "react";

const strip = (v?: string) => (v || "").replace(/^#/, "").toUpperCase();

export type HexInputProps = {
	value?: string;
	onChange?: (hex: string) => void;
	className?: string;
};

// Shared "# RRGGBB" field used by the solid header and both gradient stops.
export function HexInput({ value, onChange, className }: HexInputProps) {
	const bem = useBemCN("hex-input");
	const [local, setLocal] = React.useState<string>(strip(value));
	const focused = React.useRef(false);

	// Re-sync from the source color only while the user isn't editing, so an
	// echoed prop update doesn't clobber mid-typing.
	React.useEffect(() => {
		if (!focused.current) setLocal(strip(value));
	}, [value]);

	const handleChange = React.useCallback(
		(v: string) => {
			const clean = v.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
			setLocal(clean);
			// ponytail: only emit a complete 3- or 6-digit hex so mid-typing
			// doesn't push invalid colors into react-color.
			if (clean.length === 3 || clean.length === 6) onChange?.(`#${clean}`);
		},
		[onChange],
	);

	return (
		<Input
			{...bem(null, null, className)}
			prepend="#"
			value={local}
			onValueChange={handleChange}
			onFocus={() => {
				focused.current = true;
			}}
			onBlur={() => {
				focused.current = false;
				setLocal(strip(value));
			}}
		/>
	);
}
